import React, { useState, useEffect, useCallback } from 'react';
import { getFeaturesByTag } from '../services/featureService';
import { getPlainTextFromLexical } from '../utils/lexicalUtils';
import './TagView.css';

const TagView = ({ tag, featureGroupId, onClose }) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadFeatures = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getFeaturesByTag(tag.id);
      setFeatures(data);
    } catch (err) {
      setError('Failed to load features');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tag.id]);

  useEffect(() => {
    loadFeatures();
  }, [tag.id, loadFeatures]);

  const getTypeBadgeClass = (type) => {
    return `tag-type-badge type-${type.toLowerCase().replace(/\s+/g, '-')}`;
  };

  return (
    <div className="tag-view-container">
      <div className="tag-view-header">
        <h2>Tag Details</h2>
        <button className="close-button" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="tag-view-content">
        <div className="tag-details-section">
          <div className="detail-row">
            <label>Tag ID</label>
            <div className="detail-value tag-id">{tag.id}</div>
          </div>

          <div className="detail-row">
            <label>Tag Name</label>
            <div className="detail-value tag-name">{tag.name}</div>
          </div>

          <div className="detail-row">
            <label>Tag Type</label>
            <div className="detail-value">
              <span className={getTypeBadgeClass(tag.type)}>
                {tag.type}
              </span>
            </div>
          </div>

          <div className="detail-row">
            <label>Keywords</label>
            <div className="detail-value">
              {tag.keywords && tag.keywords.length > 0 ? (
                <div className="keywords-list">
                  {tag.keywords.map((keyword, index) => (
                    <span key={index} className="keyword-chip">{keyword}</span>
                  ))}
                </div>
              ) : (
                <span className="no-keywords">No keywords</span>
              )}
            </div>
          </div>

          <div className="detail-row">
            <label>Created</label>
            <div className="detail-value">
              {tag.created_at ? new Date(tag.created_at).toLocaleString() : 'N/A'}
            </div>
          </div>

          {tag.updated_at && tag.updated_at !== tag.created_at && (
            <div className="detail-row">
              <label>Last Updated</label>
              <div className="detail-value">
                {new Date(tag.updated_at).toLocaleString()}
              </div>
            </div>
          )}
        </div>

        <div className="tag-features-section">
          <h3>Features with this Tag ({features.length})</h3>
          
          {loading && <div className="loading">Loading features...</div>}
          {error && <div className="error">{error}</div>}
          
          {!loading && !error && (
            <>
              {features.length === 0 ? (
                <div className="no-features">
                  No features are tagged with this tag yet.
                </div>
              ) : (
                <div className="features-list">
                  {features.map(feature => (
                    <div key={feature.id} className="feature-item">
                      <div className="feature-item-header">
                        <h4 className="feature-name">{feature.name}</h4>
                        <span className="feature-id">ID: {feature.id.substring(0, 8)}...</span>
                      </div>
                      <div className="feature-description">
                        {(() => {
                          const plainText = getPlainTextFromLexical(feature.description);
                          return plainText.length > 200 
                            ? plainText.substring(0, 200) + '...' 
                            : plainText;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TagView;

